import React, { useEffect, useState } from "react";
import apiService from "../../ApiService";
import { toast } from "react-toastify";
import Select from "react-select";

const UpdateExpenseCategory = () => {
  // Get current month's first and last day
  const getCurrentMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      from: formatDate(firstDay),
      to: formatDate(lastDay),
    };
  };

  const currentMonthDates = getCurrentMonthDates();

  const [bankOptions, setBankOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [fromDate, setFromDate] = useState(currentMonthDates.from);
  const [toDate, setToDate] = useState(currentMonthDates.to);
  const [expenses, setExpenses] = useState([]);
  const [bankFilter, setBankFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const data = await apiService.fetchExpenses({
        fromDate: fromDate || null,
        toDate: toDate || null,
        pageIndex,
        pageSize,
        bankName: bankFilter || null,
        type: typeFilter || null,
        category: categoryFilter || null,
      });
      let expensesArr = [];
      if (Array.isArray(data)) {
        expensesArr = data;
      } else if (Array.isArray(data.records)) {
        expensesArr = data.records;
      } else if (Array.isArray(data.data)) {
        expensesArr = data.data;
      }
      setExpenses(expensesArr);
      setTotalPages(Number(data.totalPages || data.total_pages || 1));
      if (typeof data.page === "number") {
        setPageIndex(data.page);
      }
    } catch (error) {
      toast.error("Failed to fetch expenses");
    }
    setLoading(false);
  };

  // Collect unique filter options from loaded expenses
  const loadFilterOptions = async () => {
    try {
      const banks = await apiService.getDistinctCardValues("bank");
      const types = await apiService.getDistinctCardValues("type");
      const categories = await apiService.getDistinctCardValues("category");
      setBankOptions(Array.isArray(banks) ? banks : []);
      setTypeOptions(Array.isArray(types) ? types : []);
      setCategoryOptions(Array.isArray(categories) ? categories : []);
    } catch (error) {
      toast.error("Failed to load filter options");
    }
  };

  useEffect(() => {
    loadFilterOptions();
  }, []);

  useEffect(() => {
    if (fromDate && toDate) fetchExpenses();
    // eslint-disable-next-line
  }, [fromDate, toDate, pageIndex, bankFilter, typeFilter, categoryFilter]);

  const handleUpdateCategory = async () => {
    if (!selectedExpense || !newCategory) return;
    try {
      // Ensure id is a number and category is a string
      const id = Number(selectedExpense.id || selectedExpense.ID);
      const category = String(newCategory).trim();
      if (!id || !category) {
        toast.error("Missing id or category");
        return;
      }
      const updatePayload = { id, category };
      const res = await apiService.updateExpense(id, updatePayload);
      // Handle case where backend returns multiple JSON objects in one response
      let success = false;
      if (res && typeof res === "object" && (res.message || res.success)) {
        if (
          (typeof res.message === "string" &&
            res.message.toLowerCase().includes("success")) ||
          res.success === true
        ) {
          success = true;
        }
      }
      if (success) {
        toast.success("Expense category updated!");
        fetchExpenses();
        setSelectedExpense(null);
        setNewCategory("");
      } else {
        toast.error("Failed to update expense category");
      }
    } catch (error) {
      toast.error("Failed to update expense category");
    }
  };

  return (
    <div className="max-w-7xl mx-auto bg-white">
      {/* Enhanced Filter Section */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 mb-6 border border-purple-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span className="text-xl mr-2">🔍</span>
          Filter Expense Records
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              To Date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bank
            </label>
            <select
              className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all bg-white"
              value={bankFilter}
              onChange={(e) => {
                setBankFilter(e.target.value);
                setPageIndex(1);
              }}
            >
              <option value="">All Banks</option>
              {bankOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type
            </label>
            <select
              className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all bg-white"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPageIndex(1);
              }}
            >
              <option value="">All Types</option>
              {typeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <Select
              options={[
                { value: "", label: "All Categories" },
                ...categoryOptions.map((opt) => ({ value: opt, label: opt })),
              ]}
              value={
                categoryFilter
                  ? { value: categoryFilter, label: categoryFilter }
                  : { value: "", label: "All Categories" }
              }
              onChange={(selected) => {
                setCategoryFilter(selected?.value || "");
                setPageIndex(1);
              }}
              isClearable
              placeholder="Search or select category..."
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: "#d1d5db",
                  borderRadius: "0.5rem",
                  padding: "0.125rem",
                  "&:hover": {
                    borderColor: "#a855f7",
                  },
                }),
              }}
            />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-600">
              <span className="font-semibold">Date Range:</span> {fromDate} to{" "}
              {toDate}
            </div>
            <button
              className="text-sm bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-1.5 rounded-lg transition-all"
              onClick={() => {
                const dates = getCurrentMonthDates();
                setFromDate(dates.from);
                setToDate(dates.to);
              }}
              type="button"
            >
              📅 Reset to Current Month
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <table className="w-full border mb-4">
          <thead>
            <tr className="bg-gray-100">
              <th className="border px-2 py-1">Date</th>
              <th className="border px-2 py-1">Bank</th>
              <th className="border px-2 py-1">Type</th>
              <th className="border px-2 py-1">Amount</th>
              <th className="border px-2 py-1">Category</th>
              <th className="border px-2 py-1">Description</th>
              <th className="border px-2 py-1">Action</th>
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center">
                  No expenses found
                </td>
              </tr>
            )}
            {expenses.map((exp) => (
              <tr
                key={exp.id}
                className={
                  selectedExpense && selectedExpense.id === exp.id
                    ? "bg-blue-50"
                    : ""
                }
              >
                <td className="border px-2 py-1">
                  {exp.transaction_date || exp.date || exp.Date || ""}
                </td>
                <td className="border px-2 py-1">
                  {exp.BankName || exp.bankName || ""}
                </td>
                <td className="border px-2 py-1">
                  {exp.type || exp.Type || ""}
                </td>
                <td className="border px-2 py-1">
                  {exp.amount || exp.Amount || ""}
                </td>
                <td className="border px-2 py-1">
                  {exp.category || exp.Category || ""}
                </td>
                <td className="border px-2 py-1">
                  {exp.description || exp.Description || ""}
                </td>
                <td className="border px-2 py-1">
                  <button
                    className="text-blue-600 underline"
                    onClick={() => {
                      setSelectedExpense(exp);
                      setNewCategory(exp.category || exp.Category || "");
                    }}
                  >
                    Update
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* Pagination */}
      <div className="flex gap-2 mb-4">
        <button
          className="px-2 py-1 border rounded"
          disabled={pageIndex <= 1}
          onClick={() => setPageIndex(pageIndex - 1)}
        >
          Prev
        </button>
        <span>
          Page {pageIndex} of {totalPages}
        </span>
        <button
          className="px-2 py-1 border rounded"
          disabled={pageIndex >= totalPages}
          onClick={() => setPageIndex(pageIndex + 1)}
        >
          Next
        </button>
      </div>
      {/* Update form */}
      {selectedExpense && (
        <div className="border p-4 rounded bg-gray-50 mb-4">
          <h3 className="font-semibold mb-2">
            Update Category for Expense #{selectedExpense.id}
          </h3>
          <div className="flex gap-2 items-center mb-2">
            <span>Current Category:</span>
            <span className="font-bold">
              {selectedExpense.category || selectedExpense.Category}
            </span>
          </div>
          <div className="flex gap-2 items-center">
            <div className="min-w-[200px]">
              <Select
                options={categoryOptions.map((cat) => ({
                  label: cat,
                  value: cat,
                }))}
                value={
                  newCategory
                    ? { label: newCategory, value: newCategory }
                    : null
                }
                onChange={(option) =>
                  setNewCategory(option ? option.value : "")
                }
                placeholder="Select Category"
                isClearable
                isSearchable
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: "36px",
                    borderColor: state.isFocused ? "#2563eb" : "#d1d5db",
                    boxShadow: state.isFocused ? "0 0 0 1px #2563eb" : "none",
                    "&:hover": { borderColor: "#2563eb" },
                    borderRadius: "0.375rem", // Tailwind rounded
                    fontSize: "1rem",
                  }),
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? "#2563eb"
                      : state.isFocused
                      ? "#e0e7ff"
                      : "white",
                    color: state.isSelected ? "white" : "#1e293b",
                    fontSize: "1rem",
                  }),
                }}
                theme={(theme) => ({
                  ...theme,
                  borderRadius: 6,
                  colors: {
                    ...theme.colors,
                    primary25: "#e0e7ff",
                    primary: "#2563eb",
                  },
                })}
              />
            </div>
            <button
              className="bg-blue-600 text-white px-4 py-1 rounded"
              onClick={handleUpdateCategory}
            >
              Update
            </button>
            <button
              className="px-4 py-1 border rounded"
              onClick={() => setSelectedExpense(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdateExpenseCategory;
